import api from './client';

export type ManualStatus = 'awaiting_check' | 'checked_awaiting_payment' | 'wont_be_paid';
export type RowStatus = ManualStatus | 'paid' | 'partially_paid';
export type ExcludeReason =
  | 'no_kpi' | 'fraud' | 'duplicate' | 'bonus_abuse' | 'chargeback' | 'not_found' | 'other';

export interface Partner {
  id: string;
  name: string;
  kind: 'advertiser' | 'network';
  currency: string;
  pay_type: 'prepay' | 'postpay' | null;
  contact: string | null;
  note: string | null;
  manager_admin_id: string | null;
  manager_name: string | null;
  offer_count: number;
  wallet_count: number;
}

export interface ReconRow {
  partner_id: string;
  partner_name: string;
  kind: 'advertiser' | 'network';
  currency: string;
  pay_type: 'prepay' | 'postpay' | null;
  deal_models: string[];
  ftd_count: number;
  ftd_cents: number;
  excluded_count: number;
  excluded_cents: number;
  qual_count: number;
  qual_cents: number;
  expected_cents: number;
  approved_count: number | null;
  approved_cents: number | null;
  approved_delta_cents: number;
  paid_cents: number;
  fee_cents: number;
  paid_delta_cents: number;
  status: RowStatus;
  status_is_derived: boolean;
  status_reason: string | null;
  note: string | null;
  updated_at: string | null;
}

export interface ReconTotals {
  expected_cents: number;
  approved_cents: number;
  paid_cents: number;
  fee_cents: number;
  cut_cents: number;
  shortfall_cents: number;
  prepay_expected_cents: number;
  partners: number;
  partners_settled: number;
}

export interface ReconPeriod {
  id: string | null;
  month: string;
  status: 'open' | 'frozen' | 'closed';
  frozen_at: string | null;
  frozen_by: string | null;
  fx_note: string | null;
}

export interface ReconMonth {
  month: string;
  period: ReconPeriod;
  rows: ReconRow[];
  totals: ReconTotals;
  drift: {
    added_count: number; added_cents: number;
    changed_count: number; changed_cents: number;
  } | null;
  orphan_deposits: { count: number; cents: number };
  unallocated: { count: number; cents: number };
}

export interface ReconItem {
  id: string;
  partner_id: string;
  partner_name: string;
  click_id: string;
  offer_id: string | null;
  offer_brand: string | null;
  country: string | null;
  deal_model: string | null;
  payout_cents_frozen: number;
  converted_at: string | null;
  excluded: number;
  exclude_reason: ExcludeReason | null;
  exclude_note: string | null;
  updated_by: string | null;
}

export interface Receipt {
  id: string;
  chain: string;
  asset: string;
  tx_hash: string;
  amount_cents: number;
  allocated_cents: number;
  unallocated_cents: number;
  from_address: string | null;
  to_address: string | null;
  received_at: string;
  source: string;
  note: string | null;
  suggested_partner: string | null;
  /** Marked as none of the reconciliation's business — kept in the log, out of the totals. */
  dismissed: number;
  dismiss_reason: string | null;
  dismissed_by: string | null;
  dismissed_at: string | null;
}

export interface Allocation {
  id: string;
  receipt_id: string;
  partner_id: string;
  partner_name: string;
  month: string;
  amount_cents: number;
  fee_cents: number;
  note: string | null;
}

export const getPartners = () => api.get<Partner[]>('/admin/finance/partners');
export const syncPartners = () => api.post<{ added: number }>('/admin/finance/partners/sync');
export const updatePartner = (id: string, body: Partial<Partner>) =>
  api.patch<Partner>(`/admin/finance/partners/${id}`, body);
export const addPartnerWallet = (id: string, body: { chain: string; address: string; label?: string }) =>
  api.post(`/admin/finance/partners/${id}/wallets`, body);

export const getReconMonth = (month: string) =>
  api.get<ReconMonth>(`/admin/finance/periods/${month}`);
export const freezeMonth = (month: string) =>
  api.post<{ items: number; frozen_at: string }>(`/admin/finance/periods/${month}/freeze`);
export const refreshMonth = (month: string) =>
  api.post<{ added: number; repriced: number }>(`/admin/finance/periods/${month}/refresh`);
export const setPeriodStatus = (month: string, body: { status?: 'frozen' | 'closed'; fx_note?: string }) =>
  api.post(`/admin/finance/periods/${month}/status`, body);

export const getReconItems = (month: string, q: {
  partner_id?: string; excluded?: '0' | '1'; search?: string; page?: number; limit?: number;
}) => {
  const p = new URLSearchParams();
  if (q.partner_id) p.set('partner_id', q.partner_id);
  if (q.excluded) p.set('excluded', q.excluded);
  if (q.search) p.set('search', q.search);
  if (q.page) p.set('page', String(q.page));
  if (q.limit) p.set('limit', String(q.limit));
  return api.get<{ items: ReconItem[]; total: number; frozen: boolean }>(
    `/admin/finance/periods/${month}/items?${p.toString()}`,
  );
};

export const patchReconItems = (month: string, body: {
  ids: string[]; excluded: boolean; reason?: ExcludeReason; note?: string;
}) => api.patch<{ changed: number }>(`/admin/finance/periods/${month}/items`, body);

export interface ImportSummary {
  mode: 'exclude' | 'keep_only';
  listed: number;
  excluded: number;
  restored: number;
  not_found: string[];
  not_found_count: number;
}

export const importReconList = (month: string, body: {
  partner_id: string; text: string; mode: 'exclude' | 'keep_only';
  reason: ExcludeReason; note?: string; dry_run?: boolean;
}) => api.post<ImportSummary>(`/admin/finance/periods/${month}/items/import`, body);

export const updateReconRow = (month: string, partnerId: string, body: {
  status?: ManualStatus; status_reason?: string | null;
  approved_count?: number | null; approved_cents?: string | number | null; note?: string | null;
}) => api.patch(`/admin/finance/periods/${month}/partners/${partnerId}`, body);

/** The list we hand the advertiser. Excluded rows travel with it deliberately. */
export async function downloadReconCsv(month: string, partnerId?: string, partnerName?: string) {
  const p = new URLSearchParams();
  if (partnerId) p.set('partner_id', partnerId);
  const blob = await api.blob(`/admin/finance/periods/${month}/export.csv?${p.toString()}`);
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = `recon-${month}${partnerName ? `-${partnerName.replace(/[^\w.-]+/g, '-')}` : ''}.csv`;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(href), 0);
}

/**
 * `relevant` (default) — booked against this month plus everything still
 * unallocated, whenever it arrived: money for July lands in August.
 * `arrived` — the month's own calendar window. `all` — no filter.
 */
export type ReceiptScope = 'relevant' | 'arrived' | 'all';

export const getReceipts = (q: {
  month?: string; unallocated?: boolean; scope?: ReceiptScope; withDismissed?: boolean;
}) => {
  const p = new URLSearchParams();
  if (q.month) p.set('month', q.month);
  if (q.scope) p.set('scope', q.scope);
  if (q.unallocated) p.set('unallocated', '1');
  if (q.withDismissed) p.set('dismissed', '1');
  return api.get<{
    receipts: Receipt[];
    allocations: Allocation[];
    /** The unallocated pile split by the month the money arrived. */
    unallocated_by_month: Array<{ month: string; count: number; cents: number }>;
  }>(`/admin/finance/receipts?${p.toString()}`);
};

export const createReceipt = (body: Record<string, unknown>) =>
  api.post('/admin/finance/receipts', body);
export const importReceipts = (body: { text: string; chain?: string; asset?: string; dry_run?: boolean }) =>
  api.post<{ parsed: number; inserted: number; duplicates: number; skipped: number }>(
    '/admin/finance/receipts/import', body,
  );
export const deleteReceipt = (id: string) => api.delete(`/admin/finance/receipts/${id}`);
/** Take a hash out of the reconciliation without losing it: reason required. */
export const dismissReceipt = (id: string, body: { dismissed: boolean; reason?: string }) =>
  api.patch<Receipt>(`/admin/finance/receipts/${id}/dismiss`, body);
export const allocateReceipt = (id: string, body: {
  month: string; partner_id: string; amount: string | number; fee?: string | number; note?: string;
}) => api.post(`/admin/finance/receipts/${id}/allocations`, body);
export const deleteAllocation = (id: string) => api.delete(`/admin/finance/allocations/${id}`);

export interface WalletConfig {
  address: string;
  enabled: boolean;
  cursorMs: number | null;
  lastRun: string | null;
  lastError: string | null;
  chain: string;
  asset: string;
  contract: string;
  has_api_key: boolean;
  watched_receipts: number;
  watched_cents: number;
  last_receipt: string | null;
}

export const getWallet = () => api.get<WalletConfig>('/admin/finance/wallet');
export const updateWallet = (body: { address?: string; enabled?: boolean; reset_cursor?: boolean }) =>
  api.put<WalletConfig>('/admin/finance/wallet', body);
/**
 * Without `from`: the regular poll. With it: a backfill from that date — the
 * routine window only reaches 30 days back on a fresh wallet, so older history
 * has to be asked for explicitly.
 */
export const syncWallet = (from?: string) =>
  api.post<{ fetched: number; inserted: number; truncated?: boolean; since?: string }>(
    '/admin/finance/wallet/sync', from ? { from } : {},
  );
